import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showDeleteConfirmation } from '../../../../core/alert/sweetAlertConfig';
import { Button, Form, Card, Badge } from 'react-bootstrap';
import AddMaintenanceTaskModule from './AddMaintenanceTaskModule';
import UpdateMaintenanceTaskModule from './UpdateMaintenanceTaskModule';
import ViewMaintenanceTaskModule from './ViewMaintenanceTaskModule';
import { 
  FaUser as FaUserIcon, 
  FaMapPin as FaMapPinIcon, 
  FaCalendarAlt as FaCalendarAltIcon, 
  FaTrashAlt as FaTrashAltIcon, 
  FaEye as FaEyeIcon, 
  FaEdit as FaEditIcon 
} from 'react-icons/fa';

// Workaround for TS2786: 'Icon' cannot be used as a JSX component in React 18
const FaUser = FaUserIcon as any;
const FaMapPin = FaMapPinIcon as any;
const FaCalendarAlt = FaCalendarAltIcon as any;
const FaTrashAlt = FaTrashAltIcon as any;
const FaEye = FaEyeIcon as any;
const FaEdit = FaEditIcon as any;


/* =======================
   DTOs
======================= */
interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email: string;
  phone: string;
  userType: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}

interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  description?: string;
}

interface MaintenanceTaskDTO {
  id: number;
  locationId: number | null;
  buildingId: number | null;
  floorId: number | null;
  roomId: number | null;
  description: string;
  taskScheduleId: number | null;
  startDateTime: string;
  endDateTime: string | null;
  isRecurring: boolean;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  permanentResponsibleAllocate: boolean;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
}

/* =======================
   Nav
======================= */
const navItems = [
  { label: 'All Job Cards', icon: 'ti ti-clipboard', link: all_routes.jobCards, permission: 'ROLE_MANAGE_JOB_CARD' },
  { label: 'Maintenance Tasks', icon: 'ti ti-tool', link: all_routes.maintenanceTasks, permission: 'ROLE_ADD_TASK' },
];

const pageSize = 10;

/* =======================
   Component
======================= */
const MaintenanceTask: React.FC = () => {
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [rooms, setRooms] = useState<RoomDTO[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [maintenanceTasks, setMaintenanceTasks] = useState<MaintenanceTaskDTO[]>([]);
  const [selectedTask, setSelectedTask] = useState<MaintenanceTaskDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskPageNo, setTaskPageNo] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showUpdateTask, setShowUpdateTask] = useState(false);
  const [showViewTask, setShowViewTask] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<'description' | 'status'>('description');

  // hydration caches
  const [hydratedBuildings, setHydratedBuildings] = useState<Map<number, BuildingDTO>>(new Map());
  const [hydratedFloors, setHydratedFloors] = useState<Map<number, FloorDTO>>(new Map());
  const [hydratedRooms, setHydratedRooms] = useState<Map<number, RoomDTO>>(new Map());

  // user label cache
  const [userLabelCache, setUserLabelCache] = useState<Map<number, string>>(new Map());
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);

  // fetch guards
  const prevLocationId = useRef<number | null>(null);
  const isFetchingBuildings = useRef(false);

  // building-by-id loop guards
  const isFetchingBuildingById = useRef<Set<number>>(new Set());
  const fetchedBuildingIds = useRef<Set<number>>(new Set());
  const requestedBuildingIds = useRef<Set<number>>(new Set());
  const failedBuildingIds = useRef<Set<number>>(new Set());

  const navigate = useNavigate();

  /* =======================
     Responsive columns (1/2/3)
  ======================= */
  const useResponsiveCols = () => {
    const [cols, setCols] = React.useState<number>(() => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
      if (w >= 1200) return 3;
      if (w >= 768) return 2;
      return 1;
    });
    React.useEffect(() => {
      const onResize = () => {
        const w = window.innerWidth;
        if (w >= 1200) setCols(3);
        else if (w >= 768) setCols(2);
        else setCols(1);
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, []);
    return cols;
  };
  const cols = useResponsiveCols();
  const isStack = cols === 1;

  /* =======================
     Inline layout styles
  ======================= */
  const C = {
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as React.CSSProperties,
    topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' } as React.CSSProperties,
    breadcrumb: { fontSize: '.9rem', color: '#6b7280' } as React.CSSProperties,
    leftPanel: {
      flex: '0 0 260px', background: '#fff', border: '1px solid #eef2f7',
      borderRadius: 14, padding: 16, boxShadow: '0 4px 20px rgba(0,0,0,.06)'
    } as React.CSSProperties,
    mainWrap: (stack: boolean) => ({
      display: 'flex', gap: 16, flexWrap: 'wrap',
      flexDirection: stack ? 'column' : 'row'
    }) as React.CSSProperties,
    grid: (n: number) => ({
      display: 'grid', gap: 16, width: '100%',
      gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`
    }) as React.CSSProperties,
    kpiPill: {
      display: 'inline-flex', gap: '.5rem', alignItems: 'center',
      padding: '.5rem .75rem', borderRadius: 999, background: '#f8fafc',
      fontWeight: 600, color: '#0f172a'
    } as React.CSSProperties,
    card: {
      background: '#fff',
      border: '1px solid #eef2f7',
      borderRadius: 14,
      boxShadow: '0 6px 18px rgba(16,24,40,.06)',
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      height: '100%',
      transition: 'box-shadow .2s ease, transform .2s ease'
    } as React.CSSProperties,
    hover: { boxShadow: '0 12px 24px rgba(16,24,40,.12)', transform: 'translateY(-1px)' } as React.CSSProperties,
    title: { fontSize: '1rem', fontWeight: 800, color: '#0F172A', margin: 0 } as React.CSSProperties,
    pillRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 } as React.CSSProperties,
    pill: {
      display: 'inline-flex', gap: 6, alignItems: 'center',
      padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700
    } as React.CSSProperties,
    line: { display: 'flex', gap: 8, alignItems: 'center', color: '#475569', fontSize: 14 } as React.CSSProperties,
    icon: { width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties,
    hr: { marginTop: 8, marginBottom: 8, border: 0, borderTop: '1px solid #E5E7EB' } as React.CSSProperties,
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 } as React.CSSProperties,
    idText: { fontSize: 12, color: '#64748B' } as React.CSSProperties,
    actions: { display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
    aBtn: {
      width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    } as React.CSSProperties,
    filterLabel: { display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 } as React.CSSProperties,
    fieldRow: { display: 'flex', gap: 8 } as React.CSSProperties,
    pagBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: 12, background: '#f8fafc', borderRadius: 12 } as React.CSSProperties
  };

  /* =======================
     Helpers
  ======================= */
  const statusClass = (s: string) => {
    const v = (s || '').toLowerCase();
    if (v.includes('pending')) return 'bg-warning';
    if (v.includes('progress')) return 'bg-info';
    if (v.includes('overdue')) return 'bg-danger';
    if (v.includes('completed')) return 'bg-success';
    if (v.includes('active')) return 'bg-primary';
    return 'bg-dark';
  };

  const checkToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return token;
  }, [navigate]);

  const mapApiUserToDTO = (u: any): UserDTO | null => {
    if (!u) return null;
    const apiId = Number(u?.id ?? u?.empNumber);
    if (Number.isNaN(apiId)) return null;

    let expireDate = '';
    const rawExpire = u?.driverSpecificData?.expireDate;
    if (rawExpire) {
      const d = new Date(rawExpire);
      if (!isNaN(d.getTime())) expireDate = d.toISOString().split('T')[0];
    }

    const deptNames: string[] = Array.isArray(u?.departmentAdminSpecificData?.departmentNames)
      ? u.departmentAdminSpecificData.departmentNames
      : [];

    return {
      id: apiId,
      empNumber: String(u.empNumber ?? ''),
      name: u?.name ?? 'Unknown',
      createdAt: u?.createdAt
        ? new Date(u.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : 'N/A',
      status: u?.status ?? 'PENDING',
      email: u?.email ?? '',
      phone: u?.phone ?? '',
      userType: (u?.group ?? 'USER').replace('_K', ''),
      departmentNames: deptNames,
      licenseNumber: u?.driverSpecificData?.licenseNumber ?? '',
      licenseType: u?.driverSpecificData?.licenseType ?? '',
      expireDate,
    };
  };

  const ensureUserLabel = async (id: number | null): Promise<string> => {
    if (!id) return '—';
    if (userLabelCache.has(id)) return userLabelCache.get(id)!;

    try {
      let res = await apiDefinitions.getUsers('1', { filterType: 'id', filterValue: String(id), date: '' });
      if (res?.data?.status === 'S1000') {
        const arr: any[] = res?.data?.data?.users ?? [];
        const exact = arr.find(u => Number(u?.id) === Number(id));
        const dto = mapApiUserToDTO(exact);
        if (dto) {
          const label = dto.empNumber ? `${dto.empNumber} - ${dto.name}` : dto.name;
          setUserLabelCache(prev => new Map(prev).set(id, label));
          return label;
        }
      }

      let page = 1;
      while (true) {
        res = await apiDefinitions.getUsers(String(page), { filterType: '', filterValue: '', date: '' });
        if (res?.data?.status !== 'S1000') break;
        const arr: any[] = res?.data?.data?.users ?? [];
        if (!arr.length) break;
        const found = arr.find(u => Number(u?.id) === Number(id));
        if (found) {
          const dto = mapApiUserToDTO(found);
          if (dto) {
            const label = dto.empNumber ? `${dto.empNumber} - ${dto.name}` : dto.name;
            setUserLabelCache(prev => new Map(prev).set(id, label));
            return label;
          }
        }
        page += 1;
      }

      setUserLabelCache(prev => new Map(prev).set(id, `User ${id}`));
      return `User ${id}`;
    } catch {
      setUserLabelCache(prev => new Map(prev).set(id, `User ${id}`));
      return `User ${id}`;
    }
  };

  /* =======================
     Fetchers
  ======================= */
  const fetchLocations = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const response: AxiosResponse = await apiDefinitions.getLocations('1', payload);
      if (response.status === 200) {
        const data = response.data.data as { locations: LocationDTO[]; total: number };
        setLocations(data.locations || []);
        if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError('Failed to fetch locations. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuildings = useCallback(
    debounce(async (locationId: number) => {
      if (isFetchingBuildings.current) return;
      if (!checkToken() || !checkPermission('ROLE_GET_LOCATIONS')) {
        if (!checkPermission('ROLE_GET_LOCATIONS')) showError('You do not have permission to view buildings.');
        return;
      }

      isFetchingBuildings.current = true;
      setIsLoading(true);
      setError(null);
      try {
        const response: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(locationId);
        if (response.status === 200) {
          setBuildings(response.data.data || []);
          setSelectedBuildingId(null);
          setFloors([]); setSelectedFloorId(null);
          setRooms([]); setSelectedRoomId(null);
          if ((response.data.data || []).length === 0) setError('No buildings found for the selected location.');
        } else {
          showError(response.data?.statusDescription || 'Failed to fetch buildings');
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          showError('Unauthorized: Invalid or expired token. Please log in again.');
          localStorage.removeItem('token');
          navigate(all_routes.login);
        } else if (err.response?.status === 404) {
          setError('No buildings found for this location');
        } else {
          showError('Failed to fetch buildings. Please try again.');
        }
      } finally {
        setIsLoading(false);
        isFetchingBuildings.current = false;
      }
    }, 500),
    [checkToken, navigate]
  );

  const fetchFloors = async () => {
    if (!checkToken() || !selectedBuildingId || !checkPermission('ROLE_VIEW_FLOOR')) {
      if (!checkPermission('ROLE_VIEW_FLOOR')) showError('You do not have permission to view floors.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
      if (response.status === 200) {
        setFloors(response.data.data || []);
        setSelectedFloorId(null);
        setRooms([]); setSelectedRoomId(null);
        if ((response.data.data || []).length === 0) setError('No floors found for the selected building.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else if (err.response?.status === 404) {
        setError('No floors found for this building');
      } else {
        showError('Failed to fetch floors. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRooms = async () => {
    if (!checkToken() || !selectedFloorId || !checkPermission('ROLE_GET_ROOMS')) {
      if (!checkPermission('ROLE_GET_ROOMS')) showError('You do not have permission to view rooms.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getRoomsByFloorId(selectedFloorId);
      if (response.status === 200) {
        setRooms(response.data.data || []);
        setSelectedRoomId(null);
        if ((response.data.data || []).length === 0) setError('No rooms found for this floor.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else if (err.response?.status === 404) {
        setError('No rooms found for this floor');
      } else {
        showError('Failed to fetch rooms. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMaintenanceTasks = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      let payload: any = {};
      if (selectedRoomId) payload = { filterType: 'roomId', filterValue: String(selectedRoomId) };
      else if (selectedFloorId) payload = { filterType: 'floorId', filterValue: String(selectedFloorId) };
      else if (selectedBuildingId) payload = { filterType: 'buildingId', filterValue: String(selectedBuildingId) };
      else if (selectedLocationId) payload = { filterType: 'locationId', filterValue: String(selectedLocationId) };
      else if (searchTerm) payload = { filterType: searchType, filterValue: searchTerm };
      else payload = { filterType: '', filterValue: '' };

      const response = await apiDefinitions.getAllMaintenanceTasks(taskPageNo, payload);
      if (response.data.status === 'S1000') {
        const tasks = (response.data.data as MaintenanceTaskDTO[]).map(task => ({
          ...task,
          isRecurring: task.isRecurring ?? false,
          responsiblePersons: task.responsiblePersons ?? [],
          accountablePersons: task.accountablePersons ?? []
        }));
        setMaintenanceTasks(tasks);
        setTotalTasks(response.data.total || 0);
        if (!response.data.data || response.data.data.length === 0) {
          setError('No maintenance tasks found for the selected filters.');
        }
      } else {
        showError(response.data.statusDescription || 'Failed to fetch maintenance tasks');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else if (error.response?.status === 404) {
        setError('No maintenance tasks found for the selected filters');
      } else {
        showError(error.response?.data?.statusDescription || 'Error fetching maintenance tasks');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTask = (id: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_TASK')) {
      showError('You do not have permission to delete maintenance tasks.');
      return;
    }
    showDeleteConfirmation(async () => {
      try {
        setIsLoading(true);
        const response = await apiDefinitions.deleteMaintenanceTask(id);
        if (response.data.status === 'S1000') {
          fetchMaintenanceTasks();
        } else {
          showError(response.data.statusDescription || 'Failed to delete maintenance task');
        }
      } catch (error: any) {
        if (error.response?.status === 401) {
          showError('Unauthorized access. Your session may have expired. Please log in again.');
          localStorage.removeItem('token');
          navigate(all_routes.login);
        } else {
          showError(error.response?.data?.statusDescription || 'Error deleting maintenance task');
        }
      } finally {
        setIsLoading(false);
      }
    });
  };

  // FIXED: View handler - now properly sets selectedTask and opens modal
  const handleViewTask = async (task: MaintenanceTaskDTO) => {
    if (!checkToken()) return;
    try {
      setIsLoading(true);
      // Pre-fetch user labels for better UX
      const userIds = [
        ...(task.responsiblePersons?.map(p => p.id) ?? []),
        ...(task.accountablePersons?.map(p => p.id) ?? [])
      ];
      await Promise.all(userIds.map(id => ensureUserLabel(id)));
      setSelectedTask(task);
      setShowViewTask(true);
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError('Error preparing task view');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // FIXED: Edit handler - now properly sets selectedTask and opens modal
  const handleEditTask = (task: MaintenanceTaskDTO) => {
    if (!checkToken() || !checkPermission('ROLE_ADD_TASK')) {
      if (!checkPermission('ROLE_ADD_TASK')) showError('You do not have permission to edit tasks.');
      return;
    }
    setSelectedTask(task);
    setShowUpdateTask(true);
  };

  const handleItemPrevious = () => { if (taskPageNo > 1) setTaskPageNo(taskPageNo - 1); };
  const handleItemNext = () => { if (taskPageNo * pageSize < totalTasks) setTaskPageNo(taskPageNo + 1); };

  /* =======================
     Local lookup maps
  ======================= */
  const locationsById = useMemo(() => {
    const m = new Map<number, LocationDTO>();
    locations.forEach(l => m.set(l.id, l));
    return m;
  }, [locations]);
  const buildingsById = useMemo(() => {
    const m = new Map<number, BuildingDTO>();
    buildings.forEach(b => m.set(b.id, b));
    return m;
  }, [buildings]);
  const floorsById = useMemo(() => {
    const m = new Map<number, FloorDTO>();
    floors.forEach(f => m.set(f.id, f));
    return m;
  }, [floors]);
  const roomsById = useMemo(() => {
    const m = new Map<number, RoomDTO>();
    rooms.forEach(r => m.set(r.id, r));
    return m;
  }, [rooms]);

  /* =======================
     Name resolvers
  ======================= */
  const resolveBuilding = (task: MaintenanceTaskDTO): BuildingDTO | undefined => {
    if (task.buildingId) {
      const b = buildingsById.get(task.buildingId) || hydratedBuildings.get(task.buildingId);
      if (b) return b;
    }
    if (task.floorId) {
      const f = floorsById.get(task.floorId) || hydratedFloors.get(task.floorId);
      if (f) {
        const b = buildingsById.get(f.buildingId) || hydratedBuildings.get(f.buildingId);
        if (b) return b;
      }
    }
    if (task.roomId) {
      const r = roomsById.get(task.roomId) || hydratedRooms.get(task.roomId);
      if (r) {
        const b = buildingsById.get(r.buildingId) || hydratedBuildings.get(r.buildingId);
        if (b) return b;
      }
    }
    return undefined;
  };

  const resolveLocationName = (task: MaintenanceTaskDTO): string => {
    if (task.locationId) {
      const loc = locationsById.get(task.locationId);
      if (loc?.name) return loc.name;
    }
    const b = resolveBuilding(task);
    if (b?.locationId) {
      const loc = locationsById.get(b.locationId);
      if (loc?.name) return loc.name;
    }
    return 'Headquarters';
  };

  const resolveBuildingName = (task: MaintenanceTaskDTO): string => resolveBuilding(task)?.name ?? 'Main Building';

  const resolveFloorName = (task: MaintenanceTaskDTO): string => {
    if (task.floorId) {
      const f = floorsById.get(task.floorId) || hydratedFloors.get(task.floorId);
      if (f?.name) return f.name;
    }
    if (task.roomId) {
      const r = roomsById.get(task.roomId) || hydratedRooms.get(task.roomId);
      if (r?.floorId) {
        const f = floorsById.get(r.floorId) || hydratedFloors.get(r.floorId);
        if (f?.name) return f.name;
      }
    }
    return 'Floor 1';
  };

  const resolveRoomName = (task: MaintenanceTaskDTO): string => {
    if (task.roomId) {
      const r = roomsById.get(task.roomId) || hydratedRooms.get(task.roomId);
      if (r?.name) return r.name;
    }
    return 'Common Area';
  };

  /* =======================
     Effects
  ======================= */
  useEffect(() => { fetchLocations(); }, []);

  useEffect(() => {
    if (selectedLocationId && selectedLocationId !== prevLocationId.current && !isFetchingBuildings.current) {
      prevLocationId.current = selectedLocationId;
      fetchBuildings(selectedLocationId);
    } else if (!selectedLocationId) {
      setBuildings([]); setSelectedBuildingId(null);
      setFloors([]); setSelectedFloorId(null);
      setRooms([]); setSelectedRoomId(null);
      prevLocationId.current = null;
    }
  }, [selectedLocationId, fetchBuildings]);

  useEffect(() => {
    if (selectedBuildingId) {
      fetchFloors();
    } else {
      setFloors([]); setSelectedFloorId(null);
      setRooms([]); setSelectedRoomId(null);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedFloorId) {
      fetchRooms();
    } else {
      setRooms([]); setSelectedRoomId(null);
    }
  }, [selectedFloorId]);

  useEffect(() => {
    fetchMaintenanceTasks();
  }, [selectedLocationId, selectedBuildingId, selectedFloorId, selectedRoomId, taskPageNo, searchTerm, searchType]);

  // Prefetch labels for visible tasks
  useEffect(() => {
    if (!maintenanceTasks?.length) return;
    const run = async () => {
      setIsFetchingUsers(true);
      const ids = new Set<number>();
      maintenanceTasks.forEach(t => {
        t.responsiblePersons?.forEach(rp => ids.add(rp.id));
        t.accountablePersons?.forEach(ap => ids.add(ap.id));
      });
      await Promise.all(Array.from(ids).map(id => ensureUserLabel(id)));
      setIsFetchingUsers(false);
    };
    run();
  }, [maintenanceTasks]);

  /* ========== FIXED HYDRATION EFFECT (TypeScript Safe) ========== */
  useEffect(() => {
    if (!maintenanceTasks?.length) return;

    const needBuildings = new Set<number>();
    const needFloors = new Set<number>();
    const needRooms = new Set<number>();

    for (const task of maintenanceTasks) {
      if (task.buildingId) {
        const id = task.buildingId;
        const have = buildingsById.get(id) || hydratedBuildings.get(id);
        if (!have) needBuildings.add(id);
      }
      if (task.floorId) {
        const id = task.floorId;
        const have = floorsById.get(id) || hydratedFloors.get(id);
        if (!have) needFloors.add(id);
      }
      if (task.roomId) {
        const id = task.roomId;
        const have = roomsById.get(id) || hydratedRooms.get(id);
        if (!have) needRooms.add(id);
      }
    }

    const run = async () => {
      // ---- ROOMS
      if (needRooms.size) {
        const ids = Array.from(needRooms).filter(id => !roomsById.get(id) && !hydratedRooms.get(id));
        if (ids.length) {
          const results = await Promise.allSettled(ids.map(id => apiDefinitions.getRoomById(id)));
          const nextRooms = new Map(hydratedRooms);
          results.forEach((r) => {
            if (r.status === 'fulfilled') {
              const data = r.value?.data?.data as RoomDTO | null;
              if (data?.id) nextRooms.set(data.id, data);
            }
          });
          if (nextRooms.size !== hydratedRooms.size) setHydratedRooms(nextRooms);
        }
      }

      // implied floors from rooms
      const impliedFloors = new Set<number>(needFloors);
      hydratedRooms.forEach(r => { if (r.floorId) impliedFloors.add(r.floorId); });

      // ---- FLOORS
      if (impliedFloors.size) {
        const ids = Array.from(impliedFloors).filter(id => !floorsById.get(id) && !hydratedFloors.get(id));
        if (ids.length) {
          const results = await Promise.allSettled(ids.map(id => apiDefinitions.getFloorById(id)));
          const nextFloors = new Map(hydratedFloors);
          results.forEach((r) => {
            if (r.status === 'fulfilled') {
              const data = r.value?.data?.data as FloorDTO | null;
              if (data?.id) nextFloors.set(data.id, data);
            }
          });
          if (nextFloors.size !== hydratedFloors.size) setHydratedFloors(nextFloors);
        }
      }

      // implied buildings from floors/rooms
      const impliedBuildings = new Set<number>(needBuildings);
      hydratedFloors.forEach(f => { if (f.buildingId) impliedBuildings.add(f.buildingId); });
      hydratedRooms.forEach(r => { if (r.buildingId) impliedBuildings.add(r.buildingId); });

      // ---- BUILDINGS (guarded) - FIXED TypeScript spread operator
      if (impliedBuildings.size) {
        const buildingIds = Array.from(impliedBuildings); // Convert Set to Array first
        const ids = buildingIds.filter(id =>
          !buildingsById.get(id) &&
          !hydratedBuildings.get(id) &&
          !fetchedBuildingIds.current.has(id) &&
          !requestedBuildingIds.current.has(id) &&
          !failedBuildingIds.current.has(id)
        );

        if (ids.length) {
          ids.forEach(id => requestedBuildingIds.current.add(id));
          const results = await Promise.allSettled(
            ids.map(id =>
              apiDefinitions.getBuildingById(id)
                .then(res => ({ id, data: res?.data?.data as BuildingDTO | null }))
                .catch(() => ({ id, data: null }))
            )
          );
          ids.forEach(id => requestedBuildingIds.current.delete(id));

          const nextBuildings = new Map(hydratedBuildings);
          results.forEach(r => {
            if (r.status === 'fulfilled' && r.value?.data?.id) {
              const b = r.value.data;
              nextBuildings.set(b.id, b);
              fetchedBuildingIds.current.add(b.id); // positive cache
            } else if (r.status === 'fulfilled') {
              failedBuildingIds.current.add(r.value.id); // negative cache
            }
          });
          if (nextBuildings.size !== hydratedBuildings.size) setHydratedBuildings(nextBuildings);
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceTasks, buildingsById, floorsById, roomsById]);

  /* =======================
     BreadCrumb & KPIs
  ======================= */
  const breadcrumb: string[] = [];
  if (selectedLocationId) breadcrumb.push(locations.find(l => l.id === selectedLocationId)?.name || '');
  if (selectedBuildingId) breadcrumb.push(buildings.find(b => b.id === selectedBuildingId)?.name || '');
  if (selectedFloorId) breadcrumb.push(floors.find(f => f.id === selectedFloorId)?.name || '');
  if (selectedRoomId) breadcrumb.push(rooms.find(r => r.id === selectedRoomId)?.name || '');

  const activeCount = maintenanceTasks.filter(t => {
    const s = (t.status || '').toLowerCase();
    return !(s.includes('completed') || s.includes('cancel'));
  }).length;

  /* =======================
     FIXED TaskCard Component - Original UI Preserved
  ======================= */
  const TaskCard: React.FC<{ task: MaintenanceTaskDTO }> = ({ task }) => {
  const [hover, setHover] = useState(false);
  const [responsiblePersons, setResponsiblePersons] = useState<string[]>([]);

  useEffect(() => {
    if (task.responsiblePersons?.length) {
      const labels = task.responsiblePersons.map(person => 
        person.empNumber ? `${person.empNumber} - ${person.name}` : person.name
      );
      setResponsiblePersons(labels);
    }
  }, [task.responsiblePersons]);

  // Global resolvers (your original logic)
  const locationName = resolveLocationName(task);
  const buildingName = resolveBuildingName(task);
  const floorName = resolveFloorName(task);
  const roomName = resolveRoomName(task);

  // NEW: Smart Status Badge Color
  const getStatusBadgeClass = (status: string) => {
    const s = status.toUpperCase();
    if (s.includes('INACTIVE')) return 'bg-danger text-white';
    if (s.includes('ACTIVE')) return 'bg-success text-white';
    if (s.includes('COMPLETED')) return 'bg-info text-white';
    if (s.includes('PENDING') || s.includes('PROGRESS')) return 'bg-warning text-dark';
    if (s.includes('OVERDUE')) return 'bg-danger text-white';
    return 'bg-secondary text-white';
  };

  return (
    <Card
      className={`task-card ${hover ? 'shadow-lg' : 'shadow-sm'}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: 'pointer', marginBottom: '1rem', borderRadius: '12px', overflow: 'hidden' }}
    >
      <Card.Body>
        {/* Header */}
        <div className="task-card-header d-flex justify-content-between align-items-start">
          <h5 className="task-card-title text-truncate" style={{ fontSize: '1.2rem', fontWeight: 'bold', margin: 0 }}>
            {task.description}
          </h5>
          <Badge 
            pill 
            className={`status-badge ${getStatusBadgeClass(task.status)}`}
            style={{ fontSize: '0.8rem', padding: '6px 12px' }}
          >
            {task.status}
          </Badge>
        </div>

        {/* Details */}
        <div className="task-card-details mt-3" style={{ fontSize: '0.95rem', color: '#555' }}>
          <div><FaMapPin className="text-muted me-2" /> <strong>Location:</strong> {locationName}</div>
          <div><FaMapPin className="text-muted me-2" /> <strong>Building:</strong> {buildingName}</div>
          {task.floorId && <div><FaMapPin className="text-muted me-2" /> <strong>Floor:</strong> {floorName}</div>}
          {task.roomId && <div><FaMapPin className="text-muted me-2" /> <strong>Room:</strong> {roomName}</div>}
        </div>

        {/* Responsible Persons */}
        {responsiblePersons.length > 0 && (
          <div className="responsible-persons mt-3">
            <strong style={{ fontSize: '0.9rem' }}>Responsible Persons:</strong>
            <ul className="list-unstyled mt-1 mb-0">
              {responsiblePersons.map((label, idx) => (
                <li key={idx} style={{ fontSize: '0.88rem', color: '#2c3e50' }}>
                  <FaUser className="text-info me-2" /> {label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="task-card-footer d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
          <span className="task-id text-muted" style={{ fontSize: '0.85rem' }}>
            ID: #{task.id}
          </span>
          <div className="actions">
            <Button 
              variant="outline-info" 
              size="sm" 
              className="me-2"
              onClick={(e) => {
                e.stopPropagation();
                handleViewTask(task);
              }}
            >
              <FaEye /> View
            </Button>

            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="me-2"
              onClick={(e) => {
                e.stopPropagation();
                handleEditTask(task);
              }} 
              disabled={!checkPermission('ROLE_ADD_TASK')}
            >
              <FaEdit /> Edit
            </Button>

            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTask(task.id);
              }} 
              disabled={!checkPermission('ROLE_ADD_TASK')}
            >
              <FaTrashAlt /> Delete
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

  /* =======================
     Render - ORIGINAL UI PRESERVED
  ======================= */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="row">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-8">
                <h4 className="page-title">Facility Maintenance/Job Card/Maintenance Task</h4>
              </div>
              <div className="col-4 text-end">
                <div className="head-icons"><CollapseHeader /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div className="card-body" style={{ paddingTop: 10, paddingBottom: 6 }}>
            <ul className="nav nav-tabs nav-tabs-bottom" style={{ display: 'flex', gap: 12 }}>
              {navItems.map((item, idx) =>
                item.permission && checkPermission(item.permission) ? (
                  <li className="nav-item me-item" key={idx}>
                    <Link to={item.link} className={`nav-link px-0 ${idx === 1 ? 'active' : ''}`}>
                      <i className={item.icon}></i> {item.label}
                    </Link>
                  </li>
                ) : null
              )}
            </ul>
          </div>
        </div>

        {/* KPI + New */}
        <div className="card">
          <div className="card-body" style={C.topBar}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={C.kpiPill}>🧾 Total Tasks <span className="text-info"> {totalTasks}</span></span>
              <span style={C.kpiPill}>✅ Active <span className="text-success">{activeCount}</span></span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Button variant="success" onClick={() => setShowCreateTask(true)} disabled={!checkPermission('ROLE_ADD_TASK')}>
                + New Task
              </Button>
            </div>
          </div>
        </div>

        {/* Main */}
        <div style={C.mainWrap(isStack)}>
          {/* Filters */}
          <div style={C.leftPanel}>
            <h6 className="mb-3">Smart Filters</h6>

            <div className="mb-3">
              <small style={C.filterLabel}>Location</small>
              <select
                className="form-select"
                value={selectedLocationId || ''}
                onChange={e => {
                  const value = Number(e.target.value) || null;
                  setSelectedLocationId(value);
                }}
                disabled={locations.length === 0}
              >
                <option value="">All Locations</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>

            <div className="mb-3">
              <small style={C.filterLabel}>Building</small>
              <select
                className="form-select"
                value={selectedBuildingId || ''}
                onChange={e => setSelectedBuildingId(Number(e.target.value) || null)}
                disabled={buildings.length === 0 || !selectedLocationId}
              >
                <option value="">Select Building</option>
                {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            <div className="mb-3">
              <small style={C.filterLabel}>Floor</small>
              <select
                className="form-control"
                value={selectedFloorId || ''}
                onChange={e => setSelectedFloorId(Number(e.target.value) || null)}
                disabled={floors.length === 0 || !selectedBuildingId}
              >
                <option value="">Select Floor</option>
                {floors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>

            <div className="mb-3">
              <small style={C.filterLabel}>Room</small>
              <select
                className="form-select"
                value={selectedRoomId || ''}
                onChange={e => setSelectedRoomId(Number(e.target.value) || null)}
                disabled={rooms.length === 0 || !selectedFloorId}
              >
                <option value="">Select Room</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            <div style={C.fieldRow}>
              <Form.Control
                type="text"
                placeholder={`Search by ${searchType}`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Form.Select
                value={searchType}
                onChange={e => setSearchType(e.target.value as 'description' | 'status')}
                style={{ maxWidth: 160 }}
              >
                <option value="description">Description</option>
                <option value="status">Status</option>
              </Form.Select>
            </div>
          </div>

          {/* Cards */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isLoading || isFetchingUsers ? (
              <div className="text-center my-4">
                <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : maintenanceTasks.length === 0 ? (
              <div className="alert alert-info">No maintenance tasks found for the selected filters.</div>
            ) : (
              <>
                <div style={C.grid(cols)}>
                  {maintenanceTasks.map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}
                </div>

                {/* Pagination */}
                <div style={C.pagBar}>
                  <Button variant="secondary" onClick={handleItemPrevious} disabled={taskPageNo === 1 || isLoading}>‹ Previous</Button>
                  <span>
                    Showing {Math.min((taskPageNo - 1) * pageSize + 1, totalTasks)} to {Math.min(taskPageNo * pageSize, totalTasks)} of {totalTasks}
                  </span>
                  <Button variant="secondary" onClick={handleItemNext} disabled={taskPageNo * pageSize >= totalTasks || isLoading}>Next ›</Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modals - FIXED to clear selectedTask on close */}
        {showCreateTask && (
          <AddMaintenanceTaskModule
            show={showCreateTask}
            onHide={() => setShowCreateTask(false)}
            onSuccess={fetchMaintenanceTasks}
            locations={locations}
            buildings={buildings}
            floors={floors}
            rooms={rooms}
          />
        )}
        {showUpdateTask && selectedTask && (
          <UpdateMaintenanceTaskModule
            show={showUpdateTask}
            onHide={() => {
              setShowUpdateTask(false);
              setSelectedTask(null); // Clear selected task
            }}
            task={selectedTask}
            onSuccess={fetchMaintenanceTasks}
            locations={locations}
          />
        )}
        {showViewTask && selectedTask && (
          <ViewMaintenanceTaskModule
            show={showViewTask}
            onHide={() => {
              setShowViewTask(false);
              setSelectedTask(null); // Clear selected task
            }}
            task={selectedTask!}
            locations={locations}
            buildings={buildings}
            floors={floors}
            rooms={rooms}
          />
        )}
      </div>
    </div>
  );
};

export default MaintenanceTask;